import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import Sidebar from "../components/Sidebar";
import { Topbar, PageError, friendlyError } from "../components";

interface Tenant {
  id: string;
  slug: string;
  name: string;
  plan: string;
  is_active: boolean;
  created_at: string;
  user_count: number;
  connector_count: number;
  storage_bytes: number;
  jobs_last_30d: number;
  last_backup_at: string | null;
}

interface Stats {
  total_tenants: number;
  active_tenants: number;
  suspended_tenants: number;
  total_users: number;
  total_connectors: number;
  total_storage_bytes: number;
  jobs_last_30d: number;
  plans_breakdown: Record<string, number> | null;
}

const PLAN_COLOURS: Record<string, string> = {
  free:       "var(--text3)",
  sme:        "#3B82F6",
  practice:   "var(--teal)",
  enterprise: "#8B5CF6",
};

export default function AdminTenantsPage() {
  const { get } = useApi();
  const navigate = useNavigate();

  const [stats, setStats]       = useState<Stats | null>(null);
  const [tenants, setTenants]   = useState<Tenant[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading]   = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  // Load stats
  useEffect(() => {
    get<{ stats: Stats }>("/api/admin/stats")
      .then(r => setStats(r.stats))
      .catch(() => {});
  }, [get]);

  // Load tenants
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (planFilter) params.set("plan", planFilter);
    if (statusFilter) params.set("status", statusFilter);
    params.set("page", String(page));

    get<{ tenants: Tenant[]; total: number }>(`/api/admin/tenants?${params}`)
      .then(r => { setTenants(r.tenants); setTotal(r.total); })
      .catch(err => setPageError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, [get, search, planFilter, statusFilter, page]);

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar title="Platform Admin" subtitle="Manage tenants, plans, and usage across the platform" />
        <main className="page">
          {pageError && <PageError message={pageError} onRetry={() => window.location.reload()} />}

          {!pageError && (
            <>
              {/* Stats cards */}
              {stats && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
                  <MiniStat label="Total tenants" value={String(stats.total_tenants)} />
                  <MiniStat label="Active" value={String(stats.active_tenants)} accent="var(--teal)" />
                  <MiniStat label="Total users" value={String(stats.total_users)} />
                  <MiniStat label="Storage used" value={formatBytes(stats.total_storage_bytes)} />
                </div>
              )}

              {/* Plan breakdown */}
              {stats?.plans_breakdown && (
                <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                  {Object.entries(stats.plans_breakdown).map(([plan, count]) => (
                    <span
                      key={plan}
                      style={{
                        fontSize: 11, fontFamily: "var(--mono)",
                        padding: "3px 10px", borderRadius: "var(--r)",
                        background: "var(--surface2)", color: PLAN_COLOURS[plan] ?? "var(--text2)",
                        fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
                      }}
                    >
                      {plan}: {count}
                    </span>
                  ))}
                </div>
              )}

              {/* Filters */}
              <div className="card" style={{ padding: "12px 16px", marginBottom: 16 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="text"
                    placeholder="Search by name or slug..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    className="form-input"
                    style={{ width: 240, fontSize: 13 }}
                  />
                  <select
                    value={planFilter}
                    onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
                    className="form-input"
                    style={{ width: 140, fontSize: 13 }}
                  >
                    <option value="">All plans</option>
                    <option value="free">Free</option>
                    <option value="sme">SME</option>
                    <option value="practice">Practice</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                  <select
                    value={statusFilter}
                    onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                    className="form-input"
                    style={{ width: 140, fontSize: 13 }}
                  >
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <span style={{ fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)", marginLeft: "auto" }}>
                    {total} tenant{total !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              {/* Tenants table */}
              <div className="card" style={{ overflow: "hidden" }}>
                {loading ? (
                  <div style={{ textAlign: "center", color: "var(--text3)", padding: "40px 0" }}>Loading...</div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>Plan</th>
                        <th>Status</th>
                        <th>Users</th>
                        <th>Connectors</th>
                        <th>Storage</th>
                        <th>Jobs (30d)</th>
                        <th>Last backup</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenants.length === 0 && (
                        <tr><td colSpan={8} className="td-empty">No tenants found</td></tr>
                      )}
                      {tenants.map(t => (
                        <tr
                          key={t.id}
                          className="tr-clickable"
                          onClick={() => navigate(`/admin/tenants/${t.id}`)}
                        >
                          <td>
                            <div style={{ fontWeight: 500, fontSize: 13 }}>{t.name}</div>
                            <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)" }}>{t.slug}</div>
                          </td>
                          <td>
                            <span style={{
                              fontSize: 11, fontFamily: "var(--mono)", fontWeight: 600,
                              color: PLAN_COLOURS[t.plan] ?? "var(--text2)",
                              textTransform: "uppercase",
                            }}>
                              {t.plan}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${t.is_active ? "badge-success" : "badge-fail"}`}>
                              {t.is_active ? "active" : "suspended"}
                            </span>
                          </td>
                          <td className="td-mono">{t.user_count}</td>
                          <td className="td-mono">{t.connector_count}</td>
                          <td className="td-mono">{formatBytes(t.storage_bytes)}</td>
                          <td className="td-mono">{t.jobs_last_30d}</td>
                          <td className="td-mono">
                            {t.last_backup_at
                              ? new Date(t.last_backup_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                              : "Never"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: 12, fontFamily: "var(--mono)", color: "var(--text3)", lineHeight: "32px" }}>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function MiniStat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="card" style={{ padding: "14px 16px" }}>
      <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: accent ?? "var(--text)", fontFamily: "var(--mono)" }}>
        {value}
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
